import * as THREE from "three";
import MaterialAsset, { NewMaterialParams } from './MaterialAsset';
import { assert } from "./utilities/ErrorHandling";
import TransformUtils from "./utilities/TransformUtils";
import Utils from "./Utilities";
import Engine from "./Engine";
import Callout from "./Callout";
import { getUuidv4 } from "./utilities/Misc";
import AssetsManager from "./AssetsManager";
import HighlightSphere from "./HighlightSphere";
import store, { StoreActions } from '../state/store';
import { PrimitiveTypes, defaultBillboardObject, MaterialTypes, AlphaModes, AssetTypes} from "../globals";

class MediaPanel extends THREE.Mesh
{
	center: any;
	constructor(params)
	{
		let defaultScale = params.scale ?? 0.1;

		const assetInfo = params.assetId ? AssetsManager.getAssetInfo(params.assetId) : null;
		let texture;
		if (params.assetId)
		{
			switch(assetInfo.type)
			{
				case AssetTypes.Image:
					defaultScale = store.getState().preferencesModule.editorPreferences.imageImportHeight;
					break;
				case AssetTypes.Video:
					defaultScale = store.getState().preferencesModule.editorPreferences.videoImportHeight;
					break;
			}			
		}
		super(Utils.getPrimitiveGeometry(PrimitiveTypes.Plane, defaultScale).clone());

		assert(params.assetId || params.aspect);
		let aspect = params.aspect;
		if (params.assetId)
		{
			if (assetInfo.type === AssetTypes.Image)
			{
				texture = MaterialAsset.getTexture(params.assetId);
				aspect = texture.image.width / texture.image.height;
			}
			else
			{
				texture = MaterialAsset.getVideoTexture(params.assetId);
				aspect = texture.image.videoWidth / texture.image.videoHeight;
			}

			if (assetInfo.name)
			{
				const idx = assetInfo.name.lastIndexOf('.');
				if (idx > 0)
				{
					this.name = assetInfo.name.substring(0, idx);
				}
				else
				{
					this.name = assetInfo.name;
				}
			}
			else
			{
				this.name = assetInfo.type === AssetTypes.Image ? "Image Panel" : "Video Panel";
			}
		}

		if (aspect >= 1.0) 	this.geometry.scale(aspect, 1, 1);
		else 				this.geometry.scale(1, 1, 1.0 / aspect);

		this.userData.mediaPanel = {
			aspect: aspect,
			scale: defaultScale,
		};

		// WC20-2744: pre-populate rotationOffset to ensure billboarding works out of the box
		this.userData.billboard = JSON.parse(JSON.stringify(defaultBillboardObject));
		this.userData.billboard["offsetRotation"] = { x: 90, y: 0, z: 0 };

		if(params.createMaterial && params.assetId)
		{
			this.createMaterial(assetInfo, texture);
		}
	}

	createMaterial(assetInfo, texture)
	{
		if (!this.userData.guid) this.userData.guid = getUuidv4();
		const materialParams: NewMaterialParams = { 
			name: this.name + " Material",
			type: MaterialTypes.Standard,
			isDefaultMaterial: false,
		};
		const materialAsset = new MaterialAsset(materialParams);
		let materialType;
		if (assetInfo.type === 'image')
		{
			let containsTransparency = false;
			const extension = assetInfo.extension ?? Utils.getFileExtension(assetInfo.name);
			if(extension === 'png') // WC20-1822: detect if texture contains any transparency. set material alphaMode accordingly
			{
				const renderTarget = new THREE.WebGLRenderTarget( texture.image.width, texture.image.height, {
					generateMipmaps: false, format: THREE.RGBAFormat, stencilBuffer: false, depthBuffer: false
				} );
				const triangleGeometry = new THREE.BufferGeometry();
				triangleGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1.0, -1.0, 3.0, -1.0, -1.0, 3.0]), 2));
				const camera = new THREE.PerspectiveCamera(60, texture.image.width / texture.image.height, 0.01, 10000);

				const transparencyCheckerMaterial = new THREE.RawShaderMaterial({
					transparent: true,
					vertexShader: 
						`precision mediump float;
						attribute vec2 position;
						varying mediump vec2 v_uv;

						void main() {
							gl_Position = vec4(position, 1.0, 1.0);
							// Convert position from [-1, 1] to [0, 1] for texture coordinates.
							v_uv = position * 0.5 + 0.5;
						}`,
					fragmentShader: `
						precision mediump float;
						uniform sampler2D u_texture;
						varying mediump vec2 v_uv;
					
						void main() {
							vec4 color = texture2D(u_texture, v_uv);
							if (color.a < 1.0) {
								gl_FragColor = vec4(0.0);// We found a transparent pixel!
							} else {
								gl_FragColor = vec4(1.0);
							}
						}`,
					uniforms: {
						u_texture: { value: texture },
					},
				});

				const fullScreenTriangle = new THREE.Mesh(triangleGeometry, transparencyCheckerMaterial);
				fullScreenTriangle.frustumCulled = false;
				Engine.getInstance().getRenderer().setRenderTarget( renderTarget );
				Engine.getInstance().getRenderer().render( fullScreenTriangle, camera);
				const pixelBuffer = new Uint8Array(texture.image.width * texture.image.height * 4 );
				Engine.getInstance().getRenderer().readRenderTargetPixels( renderTarget, 0, 0, texture.image.width, texture.image.height, pixelBuffer );
				Engine.getInstance().getRenderer().setRenderTarget( null );

				for (let i = 0; i < pixelBuffer.length; i += 4) 
				{
					if (pixelBuffer[i] === 0) 
					{
						containsTransparency = true;
						break;
					}
				}

				renderTarget.dispose();
				triangleGeometry.dispose();
			}

			if(containsTransparency) 
			{
				materialAsset.setAlphaMode(AlphaModes.Blend);
				store.dispatch(StoreActions.material.setMaterialProperty({
					id: materialAsset.guid,
					value: AlphaModes.Blend,
					property: 'alphaMode',
					shouldSubmit: false
				}));
			}

			materialType = MaterialTypes.DiffuseUnlit;
			materialAsset.setProperty('map', texture);
			store.dispatch(StoreActions.material.setMaterialProperty({
				id: materialAsset.guid,
				value: assetInfo.id,
				property: 'map',
				shouldSubmit: false
			}));
		}
		else
		{
			materialType = MaterialTypes.Video;
			materialAsset.setProperty("video", texture);
			store.dispatch(StoreActions.material.setMaterialProperty({
				id: materialAsset.guid,
				value: assetInfo.id,
				property: 'video',
				shouldSubmit: false
			}));
		}

		store.dispatch(StoreActions.material.setMaterialProperty({
			id: materialAsset.guid,
			value: materialType,
			property: 'type',
			shouldSubmit: false
		}));
		MaterialAsset.assignMaterialAssetToObject(materialAsset, this);
	}

	clone()
	{
		return new MediaPanel({ aspect: this.userData.mediaPanel.aspect, scale: this.userData.mediaPanel.scale }).copy(this) as this;
	}

	copy(source, recursive = true)
	{
		THREE.Object3D.prototype.copy.call( this, source, false );
		if ( source.center !== undefined ) this.center.copy( source.center );

		if (source.userData.pivot)
		{
			TransformUtils.applyPivot(this, source.userData.pivot.relativeOffset);
		}

		if (recursive === true)
		{
			for (let i = 0; i < source.children.length; i++)
			{
				const child = source.children[i];
				if (!child.userData.hideInHierarchy || child instanceof Callout || child instanceof HighlightSphere)
				{
					this.add(child.clone());
				}
			}
		}

		return this;
	}
}

export default MediaPanel;
